import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export function useCurrencies(activeOnly: boolean = true) {
  return useQuery({
    queryKey: ['currencies', activeOnly],
    queryFn: () => api.getCurrencies(activeOnly),
    staleTime: 60 * 60 * 1000, // 1 hour - currencies don't change often
  });
}

export function useActiveCurrencies() {
  return useQuery({
    queryKey: ['currencies', 'active'],
    queryFn: () => api.getActiveCurrencies(),
    staleTime: 60 * 60 * 1000,
  });
}

export function useExchangeRates(options: {
  from_currency?: string;
  to_currency?: string;
  active_only?: boolean;
  page?: number;
  page_size?: number;
} = {}) {
  return useQuery({
    queryKey: ['exchange-rates', options],
    queryFn: () => api.getExchangeRates(options),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCurrencyConversion() {
  return useMutation({
    mutationFn: ({ amount, fromCurrency, toCurrency, date }: { 
      amount: number;
      fromCurrency: string;
      toCurrency: string;
      date?: string;
    }) => api.convertCurrency(amount, fromCurrency, toCurrency, date),
  });
}

export function useLatestRate() {
  return useMutation({
    mutationFn: ({ fromCurrency, toCurrency }: { 
      fromCurrency: string;
      toCurrency: string;
    }) => api.getLatestRate(fromCurrency, toCurrency),
  });
}

export function useCreateExchangeRate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => api.createExchangeRate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exchange-rates'] });
      queryClient.invalidateQueries({ queryKey: ['currencies'] });
    },
  });
}
